/**
 * SB5-B.1 — Per-row progress indicator for the Contracts list.
 *
 * Per Q2 — `progressPct` is BE-computed as
 *   round(100 * COUNT(positions WHERE state=COMPLETED) /
 *         NULLIF(COUNT(positions WHERE state IN
 *               (CREATED, SCHEDULED, EXECUTION, COMPLETED)), 0))
 * Returned as `0..100` OR `null` when the contract has zero
 * non-WITHDRAWN/REJECTED positions. We render an em-dash for null per
 * the spec.
 */
import { type ReactElement } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";

export function ContractProgressBar({
  value,
}: {
  value: number | null;
}): ReactElement {
  if (value === null) {
    return (
      <Box sx={{ width: "100%", textAlign: "left" }} data-progress="none">
        <Typography variant="body2" color="text.secondary" component="span">
          —
        </Typography>
      </Box>
    );
  }
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
      data-progress={clamped}
    >
      <LinearProgress
        variant="determinate"
        value={clamped}
        sx={{ flex: 1, height: 6, borderRadius: 3 }}
        aria-label={`Progress ${String(clamped)} percent`}
      />
      <Typography
        variant="caption"
        sx={{
          minWidth: 36,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clamped}%
      </Typography>
    </Box>
  );
}
